/**
 * Módulo Entrevistas — `/entrevistas`. Lo ven los tres roles y no tiene un solo
 * campo de $$.
 *
 * Esta página orquesta y no dibuja: el triage, la tabla, el formulario y los
 * insights son componentes de esta misma carpeta, y todo lo derivado sale de
 * `resumen.ts` / `insights.ts`, que son puros.
 *
 * Las dos zonas están en orden de urgencia y no de tipo de dato:
 *
 *  1. **Lo que falta registrar** — la cola de trabajo: entrevistas sin agendar,
 *     hechas sin cargar y cuestionarios a medias.
 *  2. **Lo que dicen los clientes** — el patrón, por bloque del cuestionario.
 */
import { useMemo, useState } from 'react'

import { mensajeDeError } from '../../lib/api'
import { listaEs, plural } from '../../lib/format'
import { useInterviews } from '../../lib/queries'
import { usePeriodo } from '../../shell/AppShell'
import { Button, Section, TriageBar } from '../../ui'
import { Cargando, ErrorDeCarga, Vacio } from '../comunes'
import { FormularioEntrevista } from './FormularioEntrevista'
import { InsightsPorBloque } from './InsightsPorBloque'
import { ListaEntrevistas } from './ListaEntrevistas'
import { agruparPorBloque, bloquesVacios, totalRespuestas } from './insights'
import { ordenarPorUrgencia, resumenEntrevistas } from './resumen'
import { useVinculos } from './vinculos'

export default function EntrevistasPage() {
  const { periodo } = usePeriodo()
  // Un solo "ahora" para toda la pantalla: si cada badge instanciara su propio
  // reloj, dos cifras del mismo dato podrían decir frescuras distintas.
  const [now] = useState(() => new Date())
  const [seleccionadaId, setSeleccionadaId] = useState<string | null>(null)

  const { data, isPending, isFetching, error, refetch } = useInterviews()
  const vinculos = useVinculos(periodo)

  const entrevistas = data?.entrevistas ?? []
  const totalPreguntas = data?.totalPreguntas ?? 0

  const ordenadas = useMemo(
    () => ordenarPorUrgencia(entrevistas, totalPreguntas),
    [entrevistas, totalPreguntas],
  )
  const resumen = useMemo(
    () => resumenEntrevistas(entrevistas, totalPreguntas),
    [entrevistas, totalPreguntas],
  )
  const bloques = useMemo(
    () => agruparPorBloque(data?.insights ?? [], data?.cuestionario ?? []),
    [data?.insights, data?.cuestionario],
  )
  const clientIdPorEntrevista = useMemo(
    () =>
      Object.fromEntries(entrevistas.map((e) => [e.id, e.clientId])) as Record<
        string,
        string
      >,
    [entrevistas],
  )

  const seleccionada =
    seleccionadaId === null
      ? null
      : (entrevistas.find((e) => e.id === seleccionadaId) ?? null)

  if (isPending) {
    return (
      <Section
        titulo="Entrevistas"
        bajada="Cinco bloques de tres preguntas por cliente, y el patrón que dejan entre todas."
      >
        <Cargando que="las entrevistas" />
      </Section>
    )
  }

  if (error !== null || data === undefined) {
    return (
      <Section
        titulo="Entrevistas"
        bajada="No pudimos leer las entrevistas. El resto del panel sigue funcionando."
      >
        <ErrorDeCarga
          que="las entrevistas"
          error={error}
          onRetry={() => void refetch()}
          reintentando={isFetching}
        />
      </Section>
    )
  }

  if (entrevistas.length === 0) {
    return (
      <Section
        titulo="Todavía no hay entrevistas"
        bajada={
          <>
            El cuestionario tiene <b>{totalPreguntas} preguntas</b> en{' '}
            <b>{data.cuestionario.length} bloques</b>, listo para la primera.
          </>
        }
      >
        <Vacio
          punteada
          mensaje="Ninguna entrevista planificada"
          detalle="Las entrevistas se planifican por cliente. Cuando exista la primera, aparece acá con su cuestionario."
        />
      </Section>
    )
  }

  // La más urgente que todavía se puede cargar: la primera del orden por
  // urgencia a la que le falten respuestas.
  const masUrgente =
    ordenadas.find((e) => e.respondidas < totalPreguntas) ?? null

  const vacios = bloquesVacios(bloques)
  const respuestas = totalRespuestas(bloques)
  const entrevistasQueAportan = new Set(
    bloques.flatMap((b) => b.preguntas.flatMap((p) => p.respuestas.map((r) => r.interviewId))),
  ).size

  return (
    <>
      <TriageBar
        estado={resumen.estado}
        titular={resumen.titular}
        meta={resumen.meta}
        conteos={[
          { severidad: 'critical', cantidad: resumen.pendientes, label: 'sin agendar' },
          { severidad: 'warning', cantidad: resumen.agendadas, label: 'agendadas' },
          {
            severidad: 'warning',
            cantidad: resumen.incompletas,
            label: 'cuestionarios a medias',
          },
          {
            severidad: 'ok',
            cantidad: resumen.hechas - resumen.incompletas - resumen.vacias,
            label: 'completas',
          },
        ]}
        acciones={
          masUrgente === null ? undefined : (
            <Button
              variante="primario"
              icono="lapiz"
              onClick={() => setSeleccionadaId(masUrgente.id)}
            >
              Cargar la que más urge ({masUrgente.clientNombre})
            </Button>
          )
        }
        className="-mx-5 -mt-5 mb-5"
      />

      <Section
        kicker="ZONA 1"
        titulo="Lo que falta registrar"
        bajada={
          <>
            De las <b>{resumen.total} entrevistas</b> planificadas,{' '}
            <b>{resumen.pendientes}</b> no están agendadas y{' '}
            <b>{resumen.incompletas + resumen.vacias}</b> quedaron a medias. Hay{' '}
            <b>
              {resumen.respuestas} de {resumen.respuestasPosibles}
            </b>{' '}
            respuestas cargadas.
          </>
        }
      >
        <ListaEntrevistas
          entrevistas={ordenadas}
          totalPreguntas={totalPreguntas}
          vinculos={vinculos}
          now={now}
          onRegistrar={(entrevista) => setSeleccionadaId(entrevista.id)}
        />
      </Section>

      <Section
        id="insights"
        kicker="ZONA 2"
        titulo="Lo que dicen los clientes"
        espaciado="lg"
        bajada={
          <>
            <b>{respuestas} respuestas</b> de <b>{entrevistasQueAportan}</b>{' '}
            {plural(entrevistasQueAportan, 'entrevista', 'entrevistas')},
            agrupadas por bloque para ver el patrón y no el caso.
            {vacios.length > 0 ? (
              <>
                {' '}
                Los bloques sin una sola respuesta son{' '}
                <b>{listaEs(vacios.map((b) => b.titulo))}</b>: eso es lo que hoy
                no sabemos.
              </>
            ) : null}
          </>
        }
      >
        <InsightsPorBloque
          bloques={bloques}
          clientIdPorEntrevista={clientIdPorEntrevista}
          totalEntrevistas={resumen.total}
        />
      </Section>

      {seleccionada !== null ? (
        <FormularioEntrevista
          entrevista={seleccionada}
          cuestionario={data.cuestionario}
          totalPreguntas={totalPreguntas}
          vinculos={vinculos}
          now={now}
          onClose={() => setSeleccionadaId(null)}
        />
      ) : null}
    </>
  )
}
